import React, { useEffect, useMemo, useRef, useState } from 'react'
import Text from 'antd/es/typography/Text'
import { Button, Flex, Form, Input, InputNumber, Modal, Select, Skeleton, Space, Checkbox } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { useParams } from 'react-router'
import { actions, actions as assignmentActions } from '../../../../store/reducers/assignment.reducer'
import { connect, useSelector } from 'react-redux'
import { CopyOutlined, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { copyToClipboard } from '../../../../utils/link.helper'
import { COLORS, getColorByIndex } from '../../../../utils/colors.helper'
import { getPatternTypeName, PATTERN_TYPES } from '../../../../constants/pattern-types'
import { v4 } from 'uuid'
import { openNotificationWithIcon } from '../../../../utils/notification.helper'
import { GROUPING_CAP_TYPES, GROUPING_CAP_TYPES_VALUE_TO_NAME } from '../../../../constants/grouping-cap-types'
import ModalWithEditor from '../file-drawer/modal-with-editor/ModalWithEditor'
import useProjectionCostEstimation from '../../../../hooks/useProjectionCostEstimation'
import { ASSIGNMENT_IS_TOO_BIG_FOR_PROJECTION } from '../../../../constants/response-messages'
import { getLlmVersionName } from '../../../../constants/llm-versions'
import { EditPatternModalFooter } from './EditPatternModalFooter'
import Access, {
    useAssignmentAccess,
} from '../access/Access'
import { AssignmentPermissions, Forbiddances } from '../../../../constants/assignment-permissions'
import { isEqual } from 'lodash'
import patternSelectors from '../../../../store/selectors/pattern.selectors'
import { useFetchSubmissionCriteriaDetailsQuery } from '../../../../store/apis/semantic-language.api'
import { fetchSubmissionCriteriaDetailsSelectors } from '../../../../store/apis/utils/selectors/semantic-language.api.selectors'
import userSelectors from '../../../../store/selectors/user.selectors'
import PatternIcon from '../../../../components/common/PatternIcon'
import TextEditor from '../../../../components/common/text-editor/TextEditor'
import QuillRenderer from '../../../../components/common/text-editor/QuillRenderer'
import { colorOptions } from '../../../../utils/antd.helper'
import {
    getPatternWarningLevelColor,
    getPatternWarningLevelName,
    PatternWarningLevels,
} from '../../../../constants/pattern-warning-levels'
import ConceptualLabelSelect from '../../../resources/conceptual-labels/select/ConceptualLabelSelect'
import CriteriaWithReferencesContainer from './CriteriaWithReferencesContainer'
import clusterSelectors from '../../../../store/selectors/cluster.selectors'
import { getSubmissionFromClustersById } from '../../../../store/selectors/submission.selectors'
import { useFetchOverruleReasoningsQuery } from '../../../../store/apis/overrule-reasoning.api'
import submissionSelectors from '../../../../store/selectors/submission.selectors'

const EditPatternModal = ({
    drawer,
    showEditPatternModal,
    setShowEditPatternModal,
    setEditingPattern,
    deletePattern,
    updatePattern,
    regroup,
    setQuery,
    setShowQueryEditor,
    setIdOfPatternToChange,
    projectCriteria,
    setDrawerFile,
}) => {
    const clusters = useSelector(clusterSelectors.all)
    const patterns = useSelector(patternSelectors.all)
    const editingPattern = useSelector(patternSelectors.editingPattern)
    const isShopUser = useSelector(userSelectors.isShopUser)
    const currentFileId = useSelector(submissionSelectors.openedSubmissionId)

    const { token, layerId } = useParams()
    const [form] = Form.useForm()
    const [isWaitingForPatternSave, setIsWaitingForPatternSave] = useState(false)
    const [criteriaFields, setCriteriaFields] = useState([])
    const [patternListForGutters, setPatternListForGutters] = useState([])
    const nameInputRef = useRef(null)

    const hasEditingFeedbackPermission = useAssignmentAccess(
        [AssignmentPermissions.CAN_EDIT],
        [
            Forbiddances.IS_VERSION_PUBLISHED,
            Forbiddances.IS_VERSION_NOT_LATEST,
            Forbiddances.VERSION_HAS_MBAS,
            Forbiddances.IS_PROCESSING,
        ],
    )

    const patternDetails = patterns?.find(motif => motif.motif_id === editingPattern?.id) || {}
    const patternLines = drawer.file?.motifList?.reduce((acc, motif) => {
        if (motif.motif_id === editingPattern.id) {
            for (let i = motif.start_row; i <= motif.end_row; i++) if (!acc.includes(i)) acc.push(i)
        }
        return acc
    }, [])
    const pattern = { ...editingPattern, ...patternDetails, lines: patternLines }

    const { data: criteriaDetails, isFetching: isFetchingCriteriaDetails } = useFetchSubmissionCriteriaDetailsQuery(
        { token, layerId, submissionId: drawer.file?.id },
        {
            skip: !drawer.file || pattern.type !== PATTERN_TYPES.SEMANTIC.value,
            selectFromResult: result =>
                fetchSubmissionCriteriaDetailsSelectors.selectCriteriaByPatternId(
                    { patternId: editingPattern.id },
                    result,
                ),
        },
    )
    
    // Fetch overrule reasonings data for this pattern
    const { data: overruleReasoningsData } = useFetchOverruleReasoningsQuery(
        { token, layerId, motifId: pattern.motif_id },
        { skip: pattern.type !== PATTERN_TYPES.SEMANTIC.value }
    )

    const handleEditPatternModalClose = () => {
        setEditingPattern(null)
        setShowEditPatternModal(false)
    }
    
    // Handle opening a file from the reference reasonings
    const handleOpenFile = (fileId) => {
        if (fileId && setDrawerFile) {
            setDrawerFile(getSubmissionFromClustersById(fileId, clusters))
        }
    }

    let oldCriteria
    try {
        oldCriteria = JSON.parse(pattern.query)
    } catch (e) {
        oldCriteria = null
    }
    const newCriteria = criteriaFields.map(field => field.value)
    const criteriaChanged = pattern.type === PATTERN_TYPES.SEMANTIC.value ? !isEqual(oldCriteria, newCriteria) : false

    const arrayOfCriteria = useMemo(() => [criteriaFields.map(field => field.value)], [criteriaFields])
    const projectionCostDetails = useProjectionCostEstimation(
        arrayOfCriteria,
        criteriaChanged,
        patternDetails?.lbe_data?.gpt_version,
    )

    const isAssignmentTooBigForProjection = projectionCostDetails === ASSIGNMENT_IS_TOO_BIG_FOR_PROJECTION

    // Prevent infinite rerenders for query patterns (a shit solution, but it works for now)
    const dependencies = useMemo(() => {
        // Only include isFetchingCriteriaDetails and criteriaDetails for semantic patterns
        if (pattern.type === PATTERN_TYPES.SEMANTIC.value) {
            return [patterns, editingPattern.id, form, isFetchingCriteriaDetails, criteriaDetails, overruleReasoningsData, currentFileId]
        }
        // Use minimal dependencies for query patterns
        return [patterns, editingPattern.id, form, currentFileId]
    }, [pattern.type, patterns, editingPattern.id, form, isFetchingCriteriaDetails, criteriaDetails, overruleReasoningsData, currentFileId])

    useEffect(() => {
        const pattern = patterns?.find(motif => motif.motif_id === editingPattern.id)

        if (pattern) {
            // Process the reference reasonings data and group by criterion index
            const referenceReasoningsByIndex = {}
            
            if (overruleReasoningsData?.items && overruleReasoningsData.items.length > 0) {
                // Group examples by criterion index
                overruleReasoningsData.items.forEach(example => {
                    const criterionIndex = example.criterion_ix
                    if (!referenceReasoningsByIndex[criterionIndex]) {
                        referenceReasoningsByIndex[criterionIndex] = []
                    }
                    
                    referenceReasoningsByIndex[criterionIndex].push({
                        fileId: example.file_id,
                        fileName: example.file_name || `File ${example.file_id}`,
                        isCurrent: currentFileId ? example.file_id === currentFileId : false,
                        existingOverrule: {
                            id: example.id,
                            criterion_met: example.criterion_met,
                            reasoning: example.reasoning
                        }
                    })
                })
            }
            
            const parsedCriteria =
                pattern.type === PATTERN_TYPES.SEMANTIC.value
                    ? JSON.parse(pattern.query).map((criterion, index) => ({
                          id: v4(),
                          value: criterion,
                          reasoning: isFetchingCriteriaDetails
                              ? 'Loading reasoning...'
                              : criteriaDetails[index]?.reasoning,
                          isMet: Boolean(criteriaDetails[index]?.isMet),
                          // Attach reference reasonings directly to the criterion
                          referenceReasonings: referenceReasoningsByIndex[index] || []
                      }))
                    : []
            
            // Parse feedback for semantic patterns
            let feedbackData = pattern?.feedback;
            let hitFeedback = '';
            let missFeedback = '';
            let showHitReasoning = false;
            let showMissReasoning = false;
            
            if (pattern.type === PATTERN_TYPES.SEMANTIC.value && pattern?.feedback) {
                try {
                    const parsedFeedback = JSON.parse(pattern.feedback);
                    hitFeedback = parsedFeedback.hit_feedback || '';
                    missFeedback = parsedFeedback.miss_feedback || '';
                    showHitReasoning = parsedFeedback.show_hit_reasoning || false;
                    showMissReasoning = parsedFeedback.show_miss_reasoning || false;
                } catch (e) {
                    // If parsing fails, use the feedback as hitFeedback
                    hitFeedback = pattern.feedback;
                }
            } else {
                feedbackData = pattern?.feedback;
            }
            
            form.setFieldsValue({
                name: pattern?.motif_caption,
                feedback: pattern.type !== PATTERN_TYPES.SEMANTIC.value ? feedbackData : undefined,
                hitFeedback: pattern.type === PATTERN_TYPES.SEMANTIC.value ? hitFeedback : undefined,
                missFeedback: pattern.type === PATTERN_TYPES.SEMANTIC.value ? missFeedback : undefined,
                showHitReasoning: pattern.type === PATTERN_TYPES.SEMANTIC.value ? showHitReasoning : undefined,
                showMissReasoning: pattern.type === PATTERN_TYPES.SEMANTIC.value ? showMissReasoning : undefined,
                description: pattern?.description,
                grade: pattern?.grade,
                color: pattern?.color_id % COLORS.length ?? 0,
                groupingCap: pattern?.grouping_cap ?? GROUPING_CAP_TYPES.UNLIMITED.value,
                warningLevel: pattern?.warning_level ?? PatternWarningLevels.NEUTRAL,
                conceptualLabels: pattern?.conceptual_labels
            })
            setCriteriaFields(parsedCriteria)
            
            // Only update pattern list for gutters if we have a file with motif list
            if (drawer?.file?.motifList) {
                setPatternListForGutters(
                    drawer.file.motifList.map(motif => {
                        return {
                            motifId: motif.motif_id,
                            startRow: motif.start_row,
                            endRow: motif.end_row,
                            color: getColorByIndex(motif.color_id).value,
                        }
                    }),
                )
            }
        }
    }, dependencies) // Use the dynamically determined dependencies

    const handleDeletePattern = () => {
        Modal.confirm({
            title: 'Confirm deleting',
            icon: <ExclamationCircleOutlined />,
            content: `Once you delete the pattern, there is no going back. Please be certain.`,
            okText: 'Delete',
            onOk() {
                return deletePattern(token, layerId, pattern.motif_id)
            },
            okType: 'danger',
            cancelText: 'Cancel',
        })
    }

    const handleSavePattern = async () => {
        if (pattern.type === PATTERN_TYPES.SEMANTIC.value) {
            if (criteriaChanged) {
                Modal.confirm({
                    type: 'info',
                    title: 'Confirm regrouping',
                    content:
                        'You have modified the criteria of this pattern and it will cause projection with regrouping. Would you like to continue?',
                    okText: 'Yes',
                    onOk() {
                        updatePatternAndCriteria(true, false)
                    },
                    cancelText: 'No',
                })
                return
            }
        }

        const groupingCapChanged = pattern.grouping_cap !== form.getFieldValue('groupingCap')

        if (groupingCapChanged) {
            Modal.confirm({
                type: 'info',
                title: 'Confirm regrouping',
                content:
                    'You have modified the grouping cap of this pattern and it will cause regrouping. Would you like to continue?',
                okText: 'Yes',
                onOk() {
                    updatePatternAndCriteria(false, true)
                },
                cancelText: 'No',
            })
            return
        }

        await updatePatternAndCriteria(false, false)
    }

    const updatePatternAndCriteria = async (shouldCriteriaBeUpdated, shouldRegroup) => {
        setIsWaitingForPatternSave(true)
        let allRequestsSucceed
        const colorId =
            form.getFieldValue('color') === pattern.color_id % COLORS.length
                ? pattern.color_id
                : form.getFieldValue('color')

        if (shouldCriteriaBeUpdated) {
            let updatePatternSucceed
            updatePatternSucceed = await updatePattern(
                token,
                layerId,
                pattern.motif_id,
                form.getFieldValue('name'),
                form.getFieldValue('feedback'),
                form.getFieldValue('description'),
                form.getFieldValue('grade') ?? 0,
                colorId,
                form.getFieldValue('groupingCap'),
                form.getFieldValue('warningLevel'),
                form.getFieldValue('conceptualLabels') || [],
                pattern.type,
                form.getFieldValue('hitFeedback'),
                form.getFieldValue('missFeedback'),
                form.getFieldValue('showHitReasoning'),
                form.getFieldValue('showMissReasoning'),
                criteriaFields.map(field => field.value),
                pattern.grouping_cap,
            )
            if (updatePatternSucceed) allRequestsSucceed = true
        } else if (shouldRegroup) {
            let updatePatternSucceed
            let regroupSucceed
            updatePatternSucceed = await updatePattern(
                token,
                layerId,
                pattern.motif_id,
                form.getFieldValue('name'),
                form.getFieldValue('feedback'),
                form.getFieldValue('description'),
                form.getFieldValue('grade') ?? 0,
                colorId,
                form.getFieldValue('groupingCap'),
                form.getFieldValue('warningLevel'),
                form.getFieldValue('conceptualLabels') || [],
                pattern.type,
                form.getFieldValue('hitFeedback'),
                form.getFieldValue('missFeedback'),
                form.getFieldValue('showHitReasoning'),
                form.getFieldValue('showMissReasoning'),
            )
            regroupSucceed = await regroup(token, layerId, pattern.grouping_cap)
            if (updatePatternSucceed && regroupSucceed) allRequestsSucceed = true
        } else {
            allRequestsSucceed = await updatePattern(
                token,
                layerId,
                pattern.motif_id,
                form.getFieldValue('name'),
                form.getFieldValue('feedback'),
                form.getFieldValue('description'),
                form.getFieldValue('grade') ?? 0,
                colorId,
                form.getFieldValue('groupingCap'),
                form.getFieldValue('warningLevel'),
                form.getFieldValue('conceptualLabels') || [],
                pattern.type,
                form.getFieldValue('hitFeedback'),
                form.getFieldValue('missFeedback'),
                form.getFieldValue('showHitReasoning'),
                form.getFieldValue('showMissReasoning'),
            )
        }

        if (allRequestsSucceed) {
            setShowEditPatternModal(false)
            setEditingPattern(null)
            openNotificationWithIcon('success', 'Pattern was saved successfully')
        } else {
            setIsWaitingForPatternSave(false)
        }
    }

    const handleClickEditQuery = () => {
        handleEditPatternModalClose()
        setQuery(pattern.query)
        setIdOfPatternToChange(pattern.motif_id)
        setShowQueryEditor(true)
    }

    const hasEditingPermission = useAssignmentAccess([AssignmentPermissions.CAN_EDIT])

    const groupingCapOptions = Object.values(GROUPING_CAP_TYPES).map(groupingCapType => ({
        label: groupingCapType.label,
        value: groupingCapType.value,
    }))

    const query =
        pattern.type === PATTERN_TYPES.QUERY.value && !isShopUser ? (
            <>
                <div className='d-flex-space-between'>
                    <Text className='mb-16' type='secondary'>
                        Created by query:
                    </Text>
                    <div className='d-flex-space-between'>
                        <Button
                            type='text'
                            shape='circle'
                            icon={<CopyOutlined />}
                            onClick={() => copyToClipboard(pattern.query)}
                            aria-label='Copy query to the clipboard'
                            title='Copy query to the clipboard'
                        />
                        <Access permissions={[AssignmentPermissions.CAN_EDIT]}>
                            <Button
                                type='text'
                                shape='circle'
                                icon={<EditOutlined />}
                                onClick={handleClickEditQuery}
                                aria-label='Edit query'
                                title='Edit query'
                            />
                        </Access>
                    </div>
                </div>
                <div>
                    <code>{pattern.query}</code>
                </div>
            </>
        ) : null

    const handleProjectCriteria = async criteria => {
        const projection = await projectCriteria(token, criteria, patternDetails?.lbe_data?.gpt_version, layerId, pattern.motif_id)
        if (projection) {
            const formattedPatternList = projection.reduce((acc, p) => {
                if (p.matched_snippets) {
                    p.matched_snippets.forEach(snippet => {
                        acc.push({
                            motifId: pattern.motif_id,
                            startRow: snippet.lineno,
                            endRow: snippet.end_lineno,
                            color: getColorByIndex(pattern.color_id).value,
                            tooltip: `Hit by criterion: "${p.criterion}"`,
                        })
                    })
                }
                return acc
            }, [])
            setPatternListForGutters(formattedPatternList)
        }
        return projection
    }

    let criteria
    if (pattern.type === PATTERN_TYPES.SEMANTIC.value) {
        if (hasEditingPermission) {
            criteria = (
                <CriteriaWithReferencesContainer
                    criteria={criteriaFields}
                    setCriteria={setCriteriaFields}
                    regenerateReasoning={handleProjectCriteria}
                    token={token}
                    layerId={layerId}
                    patternId={pattern.motif_id}
                    onOpenFile={handleOpenFile}
                    editable={true}
                    patternType={pattern.type}
                />
            )
        } else {
            criteria = (
                <CriteriaWithReferencesContainer
                    criteria={criteriaFields}
                    token={token}
                    layerId={layerId}
                    patternId={pattern.motif_id}
                    onOpenFile={handleOpenFile}
                    editable={false}
                    patternType={pattern.type}
                />
            )
        }
    }

    const patternTypeName = getPatternTypeName(
        pattern.type,
        pattern.lbe_data?.similarity_function,
        pattern.method,
    ).toLowerCase()

    const warningLevelOptions = Object.values(PatternWarningLevels).map(level => ({
        label: (
            <Space size={8}>
                <PatternIcon id={level} color={getPatternWarningLevelColor(level)} showId={false} />{' '}
                {getPatternWarningLevelName(level)}
            </Space>
        ),
        value: level,
    }))

    return (
        <ModalWithEditor
            title={
                <Space size={8}>
                    <PatternIcon id={pattern.motif_id} type={pattern.type} colorId={pattern.color_id} />
                    {hasEditingFeedbackPermission
                        ? `Edit ${patternTypeName} pattern`
                        : `View ${patternTypeName} pattern`}
                </Space>
            }
            open={showEditPatternModal}
            footer={
                <EditPatternModalFooter
                    drawer={drawer}
                    pattern={pattern}
                    onDeletePattern={handleDeletePattern}
                    isWaitingForPatternSave={isWaitingForPatternSave}
                    handleEditPatternModalClose={handleEditPatternModalClose}
                    criteriaChanged={criteriaChanged}
                    projectionCostDetails={projectionCostDetails}
                    isAssignmentTooBigForProjection={isAssignmentTooBigForProjection}
                />
            }
            onCancel={handleEditPatternModalClose}
            width={1200}
            patternsList={patternListForGutters || []}
            patternIdsToShow={[pattern.motif_id]}
            afterOpenChange={() => nameInputRef.current?.focus()}
        >
            {pattern ? (
                <Form
                    name='edit-pattern'
                    onFinish={handleSavePattern}
                    form={form}
                    autoComplete='off'
                    layout='vertical'
                    requiredMark={false}
                    validateTrigger={['onBlur', 'onSubmit']}
                >
                    <Form.Item label='Name' name='name'>
                        <Access
                            permissions={[AssignmentPermissions.CAN_EDIT]}
                            fallback={<Text>{pattern.motif_caption || 'This pattern has no name'}</Text>}
                        >
                            <Input placeholder='Enter name for this pattern' ref={nameInputRef} />
                        </Access>
                    </Form.Item>
                    <Form.Item name='conceptualLabels' label='Conceptual labels' tooltip='Conceptual labels identify the key  principles to be demonstrated in student work. Select labels that connect specific work patterns to broader educational concepts the student demonstrates or needs to develop.'>
                        <ConceptualLabelSelect disabled={!hasEditingPermission} />
                    </Form.Item>
                    <Form.Item label='Description' name='description'>
                        <Access
                            permissions={[AssignmentPermissions.CAN_EDIT]}
                            skipForbiddances={[
                                Forbiddances.IS_VERSION_PUBLISHED,
                                Forbiddances.IS_VERSION_NOT_LATEST,
                                Forbiddances.VERSION_HAS_MBAS,
                                Forbiddances.IS_PROCESSING,
                            ]}
                            fallback={
                                <Text>{pattern.description || 'This pattern has no description'}</Text>
                            }
                        >
                            <TextArea rows={4} placeholder='Enter description for this pattern' />
                        </Access>
                    </Form.Item>
                    {pattern.type !== PATTERN_TYPES.SEMANTIC.value ? (
                        <Form.Item name='feedback'>
                            <Access
                                permissions={[AssignmentPermissions.CAN_EDIT]}
                                skipForbiddances={[
                                    Forbiddances.IS_VERSION_PUBLISHED,
                                    Forbiddances.IS_VERSION_NOT_LATEST,
                                    Forbiddances.VERSION_HAS_MBAS,
                                    Forbiddances.IS_PROCESSING,
                                ]}
                                fallback={
                                    <QuillRenderer htmlContent={pattern.feedback || 'This pattern has no feedback'} />
                                }
                            >
                                <TextEditor label='Feedback' placeholder='Enter feedback for this pattern' />
                            </Access>
                        </Form.Item>
                    ) : (
                        <>
                            <Form.Item name='hitFeedback' extra={<Form.Item name='showHitReasoning' valuePropName='checked'>
                                <Access
                                    permissions={[AssignmentPermissions.CAN_EDIT]}
                                    skipForbiddances={[
                                        Forbiddances.IS_VERSION_PUBLISHED,
                                        Forbiddances.IS_VERSION_NOT_LATEST,
                                        Forbiddances.VERSION_HAS_MBAS,
                                        Forbiddances.IS_PROCESSING,
                                    ]}
                                    fallback={
                                        <Text>
                                            {(() => {
                                                try {
                                                    const parsedFeedback = JSON.parse(pattern.feedback || '{}');
                                                    return parsedFeedback.show_hit_reasoning ? 'Show hit reasoning: Yes' : 'Show hit reasoning: No';
                                                } catch (e) {
                                                    return 'Show hit reasoning: No';
                                                }
                                            })()}
                                        </Text>
                                    }
                                >
                                    <Checkbox>Show reasoning to students on hit</Checkbox>
                                </Access>
                            </Form.Item>}>
                                <Access
                                    permissions={[AssignmentPermissions.CAN_EDIT]}
                                    skipForbiddances={[
                                        Forbiddances.IS_VERSION_PUBLISHED,
                                        Forbiddances.IS_VERSION_NOT_LATEST,
                                        Forbiddances.VERSION_HAS_MBAS,
                                        Forbiddances.IS_PROCESSING,
                                    ]}
                                    fallback={
                                        <QuillRenderer 
                                            htmlContent={
                                                (() => {
                                                    try {
                                                        const parsedFeedback = JSON.parse(pattern.feedback || '{}');
                                                        return parsedFeedback.hit_feedback || 'No hit feedback provided';
                                                    } catch (e) {
                                                        return pattern.feedback || 'No hit feedback provided';
                                                    }
                                                })()
                                            } 
                                        />
                                    }
                                >
                                    <TextEditor label='Hit Feedback' placeholder='Enter hit feedback for this pattern' />
                                </Access>
                            </Form.Item>
                            
            
                            
                            <Form.Item name='missFeedback' extra={<Form.Item name='showMissReasoning' valuePropName='checked'>
                                <Access
                                    permissions={[AssignmentPermissions.CAN_EDIT]}
                                    skipForbiddances={[
                                        Forbiddances.IS_VERSION_PUBLISHED,
                                        Forbiddances.IS_VERSION_NOT_LATEST,
                                        Forbiddances.VERSION_HAS_MBAS,
                                        Forbiddances.IS_PROCESSING,
                                    ]}
                                    fallback={
                                        <Text>
                                            {(() => {
                                                try {
                                                    const parsedFeedback = JSON.parse(pattern.feedback || '{}');
                                                    return parsedFeedback.show_miss_reasoning ? 'Show miss reasoning: Yes' : 'Show miss reasoning: No';
                                                } catch (e) {
                                                    return 'Show miss reasoning: No';
                                                }
                                            })()}
                                        </Text>
                                    }
                                >
                                    <Checkbox>Show reasoning to students on miss</Checkbox>
                                </Access>
                            </Form.Item>}>
                                <Access
                                    permissions={[AssignmentPermissions.CAN_EDIT]}
                                    skipForbiddances={[
                                        Forbiddances.IS_VERSION_PUBLISHED,
                                        Forbiddances.IS_VERSION_NOT_LATEST,
                                        Forbiddances.VERSION_HAS_MBAS,
                                        Forbiddances.IS_PROCESSING,
                                    ]}
                                    fallback={
                                        <QuillRenderer 
                                            htmlContent={
                                                (() => {
                                                    try {
                                                        const parsedFeedback = JSON.parse(pattern.feedback || '{}');
                                                        return parsedFeedback.miss_feedback || 'No miss feedback provided';
                                                    } catch (e) {
                                                        return 'No miss feedback provided';
                                                    }
                                                })()
                                            } 
                                        />
                                    }
                                >
                                    <TextEditor label='Miss Feedback' placeholder='Enter miss feedback for this pattern' />
                                </Access>
                            </Form.Item>
                            
                            
                        </>
                    )}
                    {criteria}
                    <Space className='d-flex-space-between'>
                        <Form.Item label='Color' name='color'>
                            <Access
                                permissions={[AssignmentPermissions.CAN_EDIT]}
                                fallback={
                                    <Space size={8}>
                                        <PatternIcon id={pattern.color_id} type={pattern.type} colorId={pattern.color_id} />
                                        {getColorByIndex(pattern.color_id).label}
                                    </Space>
                                }
                            >
                                <Select 
                                    options={colorOptions} 
                                    popupMatchSelectWidth={false} 
                                    getPopupContainer={triggerNode => triggerNode.parentNode} 
                                    showSearch 
                                    optionFilterProp='label' 
                                />
                            </Access>
                        </Form.Item>
                        <Form.Item
                            label='Warning level'
                            name='warningLevel'
                            rules={[{ required: true, message: 'Please, select warning level' }]}
                        >
                            <Access
                                permissions={[AssignmentPermissions.CAN_EDIT]}
                                skipForbiddances={[
                                    Forbiddances.IS_PROCESSING,
                                    Forbiddances.VERSION_HAS_MBAS,
                                    Forbiddances.IS_VERSION_NOT_LATEST,
                                    Forbiddances.IS_VERSION_PUBLISHED,
                                ]}
                                fallback={
                                    <Space size={8}>
                                        <PatternIcon
                                            id={pattern.warning_level}
                                            color={getPatternWarningLevelColor(pattern.warning_level)}
                                        />
                                        {getPatternWarningLevelName(pattern.warning_level)}
                                    </Space>
                                }
                            >
                                <Select 
                                    options={warningLevelOptions} 
                                    popupMatchSelectWidth={false} 
                                    getPopupContainer={triggerNode => triggerNode.parentNode} 
                                    showSearch 
                                    optionFilterProp='label' 
                                />
                            </Access>
                        </Form.Item>
                    </Space>
                    <Flex justify='space-between'>
                        <Form.Item
                            label='Grade'
                            name='grade'
                            rules={[{ type: 'number', message: 'Please, input a number' }]}
                        >
                            <Access
                                permissions={[AssignmentPermissions.CAN_EDIT]}
                                fallback={<Text>{pattern.grade ?? 'This pattern has no grade'}</Text>}
                            >
                                <InputNumber />
                            </Access>
                        </Form.Item>
                        {!isShopUser && (
                            <Form.Item
                                label='Grouping cap'
                                name='groupingCap'
                                rules={[{ required: true, message: 'Please, select grouping cap' }]}
                            >
                                <Access
                                    permissions={[AssignmentPermissions.CAN_EDIT]}
                                    fallback={
                                        <Text>{GROUPING_CAP_TYPES_VALUE_TO_NAME[pattern.grouping_cap]}</Text>
                                    }
                                >
                                    <Select 
                                        options={groupingCapOptions} 
                                        popupMatchSelectWidth={false} 
                                        getPopupContainer={triggerNode => triggerNode.parentNode} 
                                        showSearch 
                                        optionFilterProp='label' 
                                    />
                                </Access>
                            </Form.Item>
                        )}
                    </Flex>
                    {pattern.type === PATTERN_TYPES.SEMANTIC.value && (
                        <Text>
                            LLM version:{' '}
                            {<code>{getLlmVersionName(patternDetails?.lbe_data?.gpt_version)}</code> || '...'}
                        </Text>
                    )}
                    {query}
                </Form>
            ) : (
                <Skeleton active />
            )}
        </ModalWithEditor>
    )
}

const mapState = ({ assignment }) => {
    return {
        showEditPatternModal: assignment.showEditPatternModal,
        drawer: assignment.drawer,
    }
}

const mapDispatch = dispatch => {
    return {
        deletePattern: (token, layerId, motifId) => {
            return dispatch(assignmentActions.deletePattern(token, layerId, motifId))
        },
        setShowEditPatternModal: value => {
            dispatch(assignmentActions.setShowEditPatternModal(value))
        },
        setShowQueryEditor: value => {
            dispatch(assignmentActions.setShowQueryEditor(value))
        },
        setQuery: value => {
            dispatch(assignmentActions.setQuery(value))
        },
        setIdOfPatternToChange: value => {
            dispatch(assignmentActions.setIdOfPatternToChange(value))
        },
        setEditingPattern: value => {
            dispatch(assignmentActions.setEditingPattern(value))
        },
        setDrawerFile: (file) => {
            dispatch(assignmentActions.setDrawerFile(file))
        },
        updatePattern: (
            token,
            layerId,
            id,
            name,
            feedback,
            description,
            grade,
            colorId,
            groupingCap,
            warningLevel,
            conceptualLabels,
            type,
            hitFeedback,
            missFeedback,
            showHitReasoning,
            showMissReasoning,
            criteria,
            oldGroupingCap,
        ) => {
            return dispatch(
                assignmentActions.updatePattern(
                    token,
                    layerId,
                    id,
                    name,
                    feedback,
                    description,
                    grade,
                    colorId,
                    groupingCap,
                    warningLevel,
                    conceptualLabels,
                    type,
                    hitFeedback,
                    missFeedback,
                    showHitReasoning,
                    showMissReasoning,
                    criteria,
                    oldGroupingCap,
                ),
            )
        },
        projectCriteria(token, criteria, llmVersion, layerId, patternId) {
            return dispatch(actions.projectCriteria(token, criteria, false, llmVersion, null, layerId, patternId))
        },
        regroup: (token, layerId, oldGroupingCap) => {
            return dispatch(actions.regroup(token, layerId, oldGroupingCap))
        },
    }
}

export default connect(mapState, mapDispatch)(EditPatternModal)
